import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Components
import {
    Grid,
    Typography,
    Container,
    Paper,
    Divider
} from "@mui/material";
import { Button } from "@gpstrackit/react-controls"
import ChipStatus from 'Components/ChipStatus';

//Views
import DataView from 'Components/DataView';
import CreateEditSmsSystem from 'Modules/smsSystems/views/.components/createEditSmsSystem';
import DeleteSmsSystem from 'Modules/smsSystems/views/.components/deleteSmsSystem';

//Security
import { HasPermission } from "Core/security";

//Actions
import { smsSystemCreateEditOpenModal, smsSystemDeleteOpenModal } from 'Redux/actions';

const SmsSystemMain = (props) => {
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    //Redux States
    const createEditSmsSystemOpenModal = useSelector(state => state.createEditSmsSystemRedux.openModal);
    const deleteSmsSystemOpenModal = useSelector(state => state.deleteSmsSystemRedux.openModal);
    const user = useSelector(state => state.securityRedux.user);
    const lang = useSelector(state => state.commonRedux.language);

    //Local States
    const [total, setTotal] = useState(0);
    const [columns, setColumns] = useState([
        {
            key: "id",
            title: messages['sms_system_main_id'],
            width: 25,
            show: true,
            sort: true,
            locked: true,
            resize: true,
            export: true,
            functionString: 'return row?.id',
            code: "sms_system_main_id",
            render: (item) => {
                return <span>{item?.id}</span>
            }
        },
        {
            key: "name",
            field: "name",
            title: messages['sms_system_main_name'],
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.name',
            code: "sms_system_main_name",
            render: (item) => {
                return <span>{item?.name}</span>
            }
        },
        {
            key: "displayName",
            field: "displayName",
            title: messages['sms_system_main_display_name'],
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.displayName',
            code: "sms_system_main_display_name",
            render: (item) => {
                return <span>{item?.displayName}</span>
            }
        },
        {
            key: "priority",
            field: "priority",
            title: messages['sms_system_main_priority'],
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.priority',
            code: "sms_system_main_priority",
            render: (item) => {
                return <span>{item?.priority}</span>
            }
        },
        {
            key: "active",
            field: "active",
            title: messages['sms_system_main_status'],
            show: true,
            sort: true,
            locked: false,
            export: true,
            functionString: `
                let lang = "${lang}"
                if(lang === "en" && row.active) {
                    return "Active"
                }
                if(lang === "en" && !row.active) {
                    return "Inactive"
                }

                if(lang === "es" && row.active) {
                    return "Activo"
                }
                if(lang === "es" && !row.active) {
                    return "Inactivo"
                }
            `,
            code: "sms_system_main_status",
            width: 150,
            render: (item) => {
                return (
                    <ChipStatus statusCode={item?.active ? 1 : 20} messages={messages} />
                )
            }
        },
    ])

    useEffect(() => {
        let currentColumns = columns.map((item) => ({
            ...item,
            title: messages[item.code],
            render: item.key === "active" ? (item) => {
                return (
                    <ChipStatus statusCode={item?.active ? 1 : 20} messages={messages} />
                )
            } : item.render,
            functionString: item.key === "active" ? `
                let lang = "${lang}"
                if(lang === "en" && row.active) {
                    return "Active"
                }
                if(lang === "en" && !row.active) {
                    return "Inactive"
                }

                if(lang === "es" && row.active) {
                    return "Activo"
                }
                if(lang === "es" && !row.active) {
                    return "Inactivo"
                }
            `
                : item.functionString
        }))
        setColumns([...currentColumns])
    }, [lang])

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Grid item xs={6}>
                        <Typography variant="h5" gutterBottom component="div">
                            {messages['sms_system_main_title']} ({total})
                        </Typography>
                    </Grid>
                    <Grid item xs={6} className="main-view-buttons">
                        <Button
                            disabled={!HasPermission(user, "smss", "c")}
                            className="btn-dark-blue"
                            onClick={(e) => dispatch(smsSystemCreateEditOpenModal(true))}
                        >
                            {messages['sms_system_main_add']}
                        </Button>
                    </Grid>
                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>
                    <DataView
                        ref={tableRef}
                        exportName="smssystems"
                        dinamicQuery
                        name="sms-system-test1"
                        entity="sms-system"
                        graphqlFunction="searchSmsIntegration"
                        graphqlSort="SmsIntegrationSortInput"
                        graphqlConditions="SmsIntegrationSearchInput"
                        customQuery="id name"
                        columns={columns}
                        hideAllInPagination
                        asyncSelect
                        searchConditions={["name", "displayName"]}
                        searchOptionValue="id"
                        searchOptionLabel="name"
                        onChangeConditions={(tags, conditions, total, newColumns, rows, sort) => {
                            let currentConditions = {}
                            let currentTags = {}
                            if (!conditions && tags) {
                                currentConditions = tableRef?.current?.conditions;
                                currentTags = tags;
                            } else {
                                currentConditions = conditions || [];
                                currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
                                if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
                            }

                            currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'name');

                            let tagsById = currentTags.filter((item) => typeof item.id === "number");
                            if (tagsById?.length > 0) {
                                //New Items
                                let currentIds = tagsById.map((item) => (item.id));
                                if (currentIds.length > 0) {
                                    currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                                }
                            }
                            let tagsByLabel = currentTags.filter(item => typeof item.id === "string")
                            if (tagsByLabel.length > 0) {
                                let currentLabels = tagsByLabel.map((item) => (item.name))
                                if (currentLabels.length > 0) {
                                    currentConditions.push({ fields: ["name"], terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                                }
                            }

                            if (!conditions && tags) tableRef?.current?.getData(null, null, null, currentConditions, newColumns);
                            else tableRef?.current?.getData(null, rows, sort, currentConditions);
                        }}
                        searchFields={["name", "displayName"]}
                        topheight={200}
                        onChangeTotal={(total) => setTotal(total)}
                        noSplit
                        onChangeColumns={(items) => setColumns(items)}
                        tableButtons={["export", 'orderColumn']}
                        noFilters
                        options={[
                            {
                                id: "edit-sms-system",
                                label: messages['edit'],
                                single: true,
                                show: true,
                                disabled: !HasPermission(user, "smss", "u"),
                                action: (items) => {
                                    dispatch(smsSystemCreateEditOpenModal(true, items[0]))
                                }
                            },
                            {
                                id: "delete-sms-system",
                                label: messages['delete'],
                                single: true,
                                show: true,
                                disabled: !HasPermission(user, "smss", "d"),
                                action: (items) => {
                                    dispatch(smsSystemDeleteOpenModal(true, items[0]))
                                }
                            }
                        ]}
                        onClickMenu={(event, item) => {
                            if (event === messages['edit']) {
                                if (HasPermission(user, "smss", "u")) {
                                    dispatch(smsSystemCreateEditOpenModal(true, item))
                                } else {
                                    NotificationManager.warning(
                                        messages['common_error_permission'],
                                        messages['sms_system_main_edit'],
                                        5000,
                                    );
                                }
                            }
                            if (event === messages['delete']) {
                                if (HasPermission(user, "smss", "d")) {
                                    dispatch(smsSystemDeleteOpenModal(true, item))
                                } else {
                                    NotificationManager.warning(
                                        messages['common_error_permission'],
                                        messages['sms_system_main_delete'],
                                        5000,
                                    );
                                }
                            }
                        }}
                        menuOptions={() => {
                            return [
                                messages['edit'],
                                messages['delete']
                            ]
                        }}
                    />
                </Grid>
            </Paper>
            {createEditSmsSystemOpenModal && <CreateEditSmsSystem tableRef={tableRef} />}
            {deleteSmsSystemOpenModal && <DeleteSmsSystem tableRef={tableRef} />}
        </Container>
    )
}
export default SmsSystemMain;